import {
  BusinessSectorWithChildrenType,
  BusinessSectorWithParentAndCountType,
  Paginated,
  PaginatedResponse,
  PaginationParams,
  QueryArgs,
  Sortable,
} from "@ac-expo/api-types";
import { isNullish } from "@ac-expo/utils";
import { InfiniteData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import { businessSectorQueryKeys } from "../query-keys/business-sector.query-keys";

async function getBusinessSectors() {
  const { data } = await privateAxios.get<BusinessSectorWithChildrenType[]>("/business-sector");
  return data;
}

export const useGetBusinessSectorsQuery = () => {
  return useQuery({
    queryKey: businessSectorQueryKeys.all,
    queryFn: getBusinessSectors,
  });
};

export type FilterBusinessSectorType = {
  searchText?: string;
  eventId?: string;
  excludeChildren?: boolean;
};

const getBusinessSectorsPaginated = async (
  params: Partial<Sortable<Paginated<FilterBusinessSectorType>>>,
) => {
  const res = await privateAxios.get<PaginatedResponse<BusinessSectorWithParentAndCountType>>(
    "/business-sector/paginated",
    { params },
  );
  return res.data;
};

export const useInfiniteBusinessSectorsQuery = (
  filter?: FilterBusinessSectorType,
  options?: Parameters<
    typeof useInfiniteQuery<
      PaginatedResponse<BusinessSectorWithParentAndCountType>,
      Error,
      InfiniteData<PaginatedResponse<BusinessSectorWithParentAndCountType>>,
      ReturnType<typeof businessSectorQueryKeys.infiniteList>,
      PaginationParams
    >
  >[0],
) => {
  return useInfiniteQuery({
    queryKey: businessSectorQueryKeys.infiniteList(filter),
    queryFn: ({ pageParam }) =>
      getBusinessSectorsPaginated({
        ...filter,
        pagination: pageParam,
      }),
    getNextPageParam: (lastPage: PaginatedResponse<BusinessSectorWithParentAndCountType>) => {
      if (
        isNullish(lastPage.pageIndex) ||
        isNullish(lastPage.pageSize) ||
        lastPage.count <= (lastPage.pageIndex + 1) * lastPage.pageSize
      )
        return undefined;

      return {
        pageIndex: lastPage.pageIndex + 1,
        pageSize: lastPage.pageSize,
      };
    },
    initialPageParam: {
      pageIndex: 0,
      pageSize: 10,
    },
    ...options,
  });
};

export const useBusinessSectorsPaginatedQuery = (
  filters: Partial<Sortable<Paginated<FilterBusinessSectorType>>>,
  options?: QueryArgs<PaginatedResponse<BusinessSectorWithParentAndCountType>>,
) => {
  return useQuery({
    queryKey: businessSectorQueryKeys.list(filters),
    queryFn: () => getBusinessSectorsPaginated(filters ?? {}),
    ...options,
  });
};
